import { useIsMobile } from "@core/shared/hooks";
import { userAtom } from "@framework/config/atoms";
import { Random } from "@mongez/reinforcements";
import { useEffect, useRef } from "react";

type UseVideoWatermarkProps = {
  playerRef: React.RefObject<any>;
  isFullScreen: boolean;
  resourceUrl: string;
};

export function useVideoWatermark({ 
  playerRef, 
  isFullScreen, 
  resourceUrl 
}: UseVideoWatermarkProps) {
  const watermarkRef = useRef<HTMLSpanElement>(null);
  const user = userAtom.useValue();
  const isMobile = useIsMobile();

  useEffect(() => {
    const randomElements = ["div", "span", "p", "article", "section"];

    const wrapperRef = playerRef.current?.rootRef.current;

    // Guests (and a user atom that was just reset on logout) have no id;
    // `user.id.toString()` used to crash the lesson page in that case.
    if (!wrapperRef || user.id === undefined || user.id === null) return;

    const createWatermark = () => {
      if (watermarkRef.current) {
        // destroy the watermark element
        if (wrapperRef.contains(watermarkRef.current)) {
          wrapperRef.removeChild(watermarkRef.current);
        }
      }

      const watermark = document.createElement(
        randomElements[Random.int(0, randomElements.length - 1)] as string,
      );

      const left = Random.int(20, 80);
      const top = Random.int(20, 80);

      const style = {
        position: isFullScreen ? "fixed" : "absolute",
        top: `${top}%`,
        left: `${left}%`,
        color: "#999",
        opacity: 0.6,
        fontSize: isMobile ? "1rem" : "1.3rem",
        zIndex: 9999,
        // Make it non selectable
        userSelect: "none",
        pointerEvents: "none",
        cursor: "default",
      };

      for (const key in style) {
        watermark.style[key] = style[key];
      }

      (watermarkRef as any).current = watermark;

      watermark.textContent = String(user.id);

      const randomAppendOrPrepend = Random.boolean();
      if (randomAppendOrPrepend) {
        wrapperRef.appendChild(watermark);
      } else {
        wrapperRef.prepend(watermark);
      }
    };

    // Create initial watermark
    createWatermark();

    const interval = setInterval(createWatermark, 10_000);

    return () => {
      clearInterval(interval);
    };
  }, [isFullScreen, resourceUrl, playerRef, user.id, isMobile]);

  return {
    watermarkRef,
  };
}
